/**
 * CtaFooterTab - CTA section and Footer management
 */
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../ui/card';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Button } from '../ui/button';
import { toast } from 'sonner';
import { Copy, Plus, Trash2, ChevronDown, ChevronRight } from 'lucide-react';
import { useState } from 'react';
import { CtaBackgroundControls } from './CtaBackgroundControls';
import { CtaActionPicker } from './CtaActionPicker';

// Generate a short URL-safe id for a custom CTA. Kept small (8 chars) so the
// resulting section id (`custom_cta_<id>`) is still readable in analytics.
function makeCustomCtaId() {
  const chars = 'abcdefghijklmnopqrstuvwxyz0123456789';
  let s = '';
  for (let i = 0; i < 8; i += 1) s += chars.charAt(Math.floor(Math.random() * chars.length));
  return `custom_cta_${s}`;
}

export function CtaFooterTab({ content, setContent }) {
  const updatePrimaryCta = (field, value) => {
    setContent(prev => ({ ...prev, primary_cta: { ...prev.primary_cta, [field]: value } }));
  };

  const updateSecondaryCta = (field, value) => {
    setContent(prev => ({ ...prev, secondary_cta: { ...prev.secondary_cta, [field]: value } }));
  };

  const updateCta = (field, value) => {
    setContent(prev => ({ ...prev, cta: { ...prev.cta, [field]: value } }));
  };

  const updateFooter = (field, value) => {
    setContent(prev => ({ ...prev, footer: { ...prev.footer, [field]: value } }));
  };

  const updateFinalWhistle = (field, value) => {
    setContent(prev => ({ ...prev, final_whistle: { ...prev.final_whistle, [field]: value } }));
  };
  const patchFinalWhistle = (partial) => setContent(prev => ({ ...prev, final_whistle: { ...prev.final_whistle, ...partial } }));
  const patchFinalWhistleSoft = (partial) => setContent(prev => ({
    ...prev,
    final_whistle: { ...prev.final_whistle, soft_ask: { ...(prev.final_whistle?.soft_ask), ...partial } },
  }));

  // Patch helpers for the CTA background-image subsection (lets the child
  // component send back a single object of changes rather than wiring one
  // callback per field).
  const patchPrimaryCta = (partial) => setContent(prev => ({ ...prev, primary_cta: { ...prev.primary_cta, ...partial } }));
  const patchSecondaryCta = (partial) => setContent(prev => ({ ...prev, secondary_cta: { ...prev.secondary_cta, ...partial } }));
  const patchCta = (partial) => setContent(prev => ({ ...prev, cta: { ...prev.cta, ...partial } }));

  // ─── Custom CTA helpers ─────────────────────────────────────────────
  // Custom CTAs are stored as an array at `content.custom_ctas`. Each entry
  // gets a dynamic id, its own copy/action/background, and a display label
  // used in the Layout tab. Creating a custom CTA also inserts its id into
  // `section_order` (before the footer) so the admin can immediately drag it
  // into position without a second step.
  const customCtas = content.custom_ctas || [];

  const addCustomCta = (seed = {}) => {
    const id = makeCustomCtaId();
    const newCta = {
      id,
      label: seed.label || 'New CTA',
      title: seed.title || '',
      subtitle: seed.subtitle || '',
      buttonText: seed.buttonText || 'Get started',
      secondaryText: seed.secondaryText || '',
      // Action, styling, bg image — copied verbatim when duplicating.
      actionType: seed.actionType || 'signup',
      actionValue: seed.actionValue || '',
      bgImageUrl: seed.bgImageUrl || '',
      bgImageOpacity: seed.bgImageOpacity ?? 35,
      bgImagePosX: seed.bgImagePosX ?? 50,
      bgImagePosY: seed.bgImagePosY ?? 50,
    };
    setContent(prev => {
      const nextCtas = [...(prev.custom_ctas || []), newCta];
      // Also insert into section_order right before the footer/contact so
      // the section shows up in the Layout tab immediately.
      const existingOrder = prev.section_order || [];
      const orderEntry = { id, label: newCta.label, visible: true };
      // Avoid dup insertion (defensive)
      const alreadyThere = existingOrder.some(s => s.id === id);
      const nextOrder = alreadyThere ? existingOrder : [...existingOrder, orderEntry];
      return { ...prev, custom_ctas: nextCtas, section_order: nextOrder };
    });
    toast.success(seed.title ? `Duplicated "${seed.label || seed.title}"` : 'New CTA section added');
    return id;
  };

  const updateCustomCta = (id, partial) => {
    setContent(prev => ({
      ...prev,
      custom_ctas: (prev.custom_ctas || []).map(c => c.id === id ? { ...c, ...partial } : c),
      // Keep the Layout-tab label in sync if the admin renamed the CTA
      section_order: (prev.section_order || []).map(s =>
        (s.id === id && partial.label) ? { ...s, label: partial.label } : s
      ),
    }));
  };

  const patchCustomCtaFactory = (id) => (partial) => updateCustomCta(id, partial);

  const deleteCustomCta = (id) => {
    setContent(prev => ({
      ...prev,
      custom_ctas: (prev.custom_ctas || []).filter(c => c.id !== id),
      section_order: (prev.section_order || []).filter(s => s.id !== id),
    }));
    toast.success('Custom CTA deleted');
  };

  // Track the ID of the most-recently-created custom CTA so we can auto-expand
  // its editor. Otherwise the user creates one and doesn't realise where it
  // lives in the list.
  const [justCreatedId, setJustCreatedId] = useState(null);
  const handleAdd = (seed) => {
    const id = addCustomCta(seed);
    setJustCreatedId(id);
    // Scroll the new editor into view next tick (once React has rendered it)
    setTimeout(() => {
      try {
        const el = document.querySelector(`[data-testid="custom-cta-editor-${id}"]`);
        if (el) el.scrollIntoView({ behavior: 'smooth', block: 'center' });
      } catch { /* noop */ }
    }, 60);
  };

  return (
    <div className="space-y-6">
      {/* Additional CTA Sections — surfaced at the TOP of the tab so admins
          returning to the CMS see their custom CTAs first, not last. */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            Additional CTA Sections
            {customCtas.length > 0 && (
              <span className="text-[10px] font-black tracking-widest uppercase px-2 py-0.5 rounded-full bg-indigo-500 text-white" data-testid="additional-cta-count-badge">
                {customCtas.length}
              </span>
            )}
          </CardTitle>
          <CardDescription>
            Create extra CTA blocks that render on the landing page and appear in the Layout tab. Duplicate an existing CTA as a starting point, or start blank. Existing custom CTAs are listed below &mdash; click a row to edit.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex flex-wrap gap-2">
            <Button variant="outline" size="sm" onClick={() => handleAdd({ label: 'New CTA' })} data-testid="add-blank-cta-btn">
              <Plus className="w-4 h-4 mr-1.5" />Create blank CTA
            </Button>
            <Button variant="outline" size="sm" onClick={() => handleAdd({ ...(content.primary_cta || {}), label: 'Copy of Primary CTA' })} data-testid="duplicate-primary-cta-btn">
              <Copy className="w-4 h-4 mr-1.5" />Duplicate Primary
            </Button>
            <Button variant="outline" size="sm" onClick={() => handleAdd({ ...(content.secondary_cta || {}), label: 'Copy of Secondary CTA' })} data-testid="duplicate-secondary-cta-btn">
              <Copy className="w-4 h-4 mr-1.5" />Duplicate Secondary
            </Button>
            <Button variant="outline" size="sm" onClick={() => handleAdd({ ...(content.cta || {}), label: 'Copy of Final CTA' })} data-testid="duplicate-final-cta-btn">
              <Copy className="w-4 h-4 mr-1.5" />Duplicate Final
            </Button>
          </div>

          {customCtas.length === 0 && (
            <p className="text-xs text-gray-500 italic">
              No additional CTAs yet. Use the buttons above to add one.
            </p>
          )}

          {customCtas.map((cta) => (
            <CustomCtaEditor
              key={cta.id}
              cta={cta}
              defaultOpen={cta.id === justCreatedId}
              onPatch={patchCustomCtaFactory(cta.id)}
              onDelete={() => { if (cta.id === justCreatedId) setJustCreatedId(null); deleteCustomCta(cta.id); }}
            />
          ))}
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Primary CTA Section</CardTitle>
          <CardDescription>First call to action — appears between content sections</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div><Label>Title</Label><Input value={content.primary_cta?.title || ''} onChange={(e) => updatePrimaryCta('title', e.target.value)} data-testid="primary-cta-title" /></div>
          <div><Label>Subtitle</Label><Input value={content.primary_cta?.subtitle || ''} onChange={(e) => updatePrimaryCta('subtitle', e.target.value)} data-testid="primary-cta-subtitle" /></div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div><Label>Button Text</Label><Input value={content.primary_cta?.buttonText || ''} onChange={(e) => updatePrimaryCta('buttonText', e.target.value)} data-testid="primary-cta-button" /></div>
            <div><Label>Secondary Text</Label><Input value={content.primary_cta?.secondaryText || ''} onChange={(e) => updatePrimaryCta('secondaryText', e.target.value)} data-testid="primary-cta-secondary" /></div>
          </div>
          <CtaActionPicker
            value={content.primary_cta}
            onChange={patchPrimaryCta}
            testIdPrefix="primary-cta"
          />
          <CtaBackgroundControls
            value={content.primary_cta}
            onChange={patchPrimaryCta}
            testIdPrefix="primary-cta"
          />
        </CardContent>
      </Card>
      <Card>
        <CardHeader>
          <CardTitle>Secondary CTA Section</CardTitle>
          <CardDescription>Optional second call to action — hidden by default, enable in Layout tab</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div><Label>Title</Label><Input value={content.secondary_cta?.title || ''} onChange={(e) => updateSecondaryCta('title', e.target.value)} data-testid="secondary-cta-title" /></div>
          <div><Label>Subtitle</Label><Input value={content.secondary_cta?.subtitle || ''} onChange={(e) => updateSecondaryCta('subtitle', e.target.value)} data-testid="secondary-cta-subtitle" /></div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div><Label>Button Text</Label><Input value={content.secondary_cta?.buttonText || ''} onChange={(e) => updateSecondaryCta('buttonText', e.target.value)} data-testid="secondary-cta-button" /></div>
            <div><Label>Secondary Text</Label><Input value={content.secondary_cta?.secondaryText || ''} onChange={(e) => updateSecondaryCta('secondaryText', e.target.value)} data-testid="secondary-cta-secondary" /></div>
          </div>
          <CtaActionPicker
            value={content.secondary_cta}
            onChange={patchSecondaryCta}
            testIdPrefix="secondary-cta"
          />
          <CtaBackgroundControls
            value={content.secondary_cta}
            onChange={patchSecondaryCta}
            testIdPrefix="secondary-cta"
          />
        </CardContent>
      </Card>
      <Card>
        <CardHeader>
          <CardTitle>Final CTA Section</CardTitle>
          <CardDescription>Last call to action before footer</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div><Label>Title</Label><Input value={content.cta?.title || ''} onChange={(e) => updateCta('title', e.target.value)} /></div>
          <div><Label>Subtitle</Label><Input value={content.cta?.subtitle || ''} onChange={(e) => updateCta('subtitle', e.target.value)} /></div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div><Label>Button Text</Label><Input value={content.cta?.buttonText || ''} onChange={(e) => updateCta('buttonText', e.target.value)} /></div>
            <div><Label>Secondary Text</Label><Input value={content.cta?.secondaryText || ''} onChange={(e) => updateCta('secondaryText', e.target.value)} /></div>
          </div>
          <CtaActionPicker
            value={content.cta}
            onChange={patchCta}
            testIdPrefix="final-cta"
          />
          <CtaBackgroundControls
            value={content.cta}
            onChange={patchCta}
            testIdPrefix="final-cta"
          />
        </CardContent>
      </Card>

      {/* Final Whistle — supplemental warm sign-off block that sits below
          the Final CTA. Disabled by default; admin opts in. */}
      <Card>
        <CardHeader>
          <CardTitle>Final Whistle (Footer CTA)</CardTitle>
          <CardDescription>Warm, lower-key sign-off that sits BELOW the Final CTA — the very last engagement attempt before the footer. Off by default.</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex items-center justify-between gap-3 border-b border-gray-100 pb-3">
            <div className="min-w-0">
              <Label className="text-sm font-medium">Show Final Whistle module</Label>
              <p className="text-xs text-gray-500 mt-0.5">Enable to render the section on the landing page.</p>
            </div>
            <input
              type="checkbox"
              checked={content.final_whistle?.enabled === true}
              onChange={(e) => updateFinalWhistle('enabled', e.target.checked)}
              className="h-5 w-9 rounded-full appearance-none bg-gray-300 checked:bg-[#007a33] relative cursor-pointer transition-colors before:content-[''] before:absolute before:top-0.5 before:left-0.5 before:h-4 before:w-4 before:rounded-full before:bg-white before:transition-transform checked:before:translate-x-4"
              data-testid="final-whistle-enabled"
            />
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <Label>Eyebrow</Label>
              <Input
                value={content.final_whistle?.eyebrow || ''}
                placeholder="ONE LAST THING"
                onChange={(e) => updateFinalWhistle('eyebrow', e.target.value)}
                data-testid="final-whistle-eyebrow-input"
              />
            </div>
            <div className="md:col-span-2">
              <Label>Background style</Label>
              <select
                value={content.final_whistle?.background_style || 'subtle'}
                onChange={(e) => updateFinalWhistle('background_style', e.target.value)}
                className="mt-1 flex h-9 w-full rounded-md border border-input bg-background px-3 text-sm"
                data-testid="final-whistle-bg-style"
              >
                <option value="subtle">Subtle (matches theme)</option>
                <option value="light">Light card</option>
                <option value="dark">Dark card</option>
                <option value="brand-green">Brand green</option>
              </select>
            </div>
          </div>

          <div>
            <Label>Headline</Label>
            <Input
              value={content.final_whistle?.headline || ''}
              placeholder="Still scrolling? You're our kind of coach."
              onChange={(e) => updateFinalWhistle('headline', e.target.value)}
              data-testid="final-whistle-headline-input"
            />
          </div>
          <div>
            <Label>Subhead</Label>
            <Input
              value={content.final_whistle?.subhead || ''}
              placeholder="Stop juggling spreadsheets and start coaching properly."
              onChange={(e) => updateFinalWhistle('subhead', e.target.value)}
              data-testid="final-whistle-subhead-input"
            />
          </div>
          <div>
            <Label>CTA button text</Label>
            <Input
              value={content.final_whistle?.cta_text || ''}
              placeholder="I'm in! Let's get started"
              onChange={(e) => updateFinalWhistle('cta_text', e.target.value)}
              data-testid="final-whistle-cta-text-input"
            />
          </div>

          <CtaActionPicker
            value={content.final_whistle}
            onChange={patchFinalWhistle}
            testIdPrefix="final-whistle"
          />

          {/* Soft secondary ask — defaults to 'none' so admin opts in later. */}
          <div className="border-t border-gray-100 pt-4">
            <Label>Soft secondary ask</Label>
            <p className="text-xs text-gray-500 mt-0.5 mb-2">For visitors who aren't ready to buy — captures them into your funnel.</p>
            <select
              value={content.final_whistle?.soft_ask?.type || 'none'}
              onChange={(e) => patchFinalWhistleSoft({ type: e.target.value })}
              className="mt-1 flex h-9 w-full rounded-md border border-input bg-background px-3 text-sm"
              data-testid="final-whistle-soft-ask-type"
            >
              <option value="none">None — primary CTA only</option>
              <option value="newsletter">Newsletter signup (email)</option>
              <option value="pdf">Free PDF download</option>
              <option value="question">30-sec coach question (email + text)</option>
            </select>

            {(content.final_whistle?.soft_ask?.type || 'none') !== 'none' && (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mt-3">
                <div>
                  <Label className="text-xs">Label / placeholder</Label>
                  <Input
                    value={content.final_whistle?.soft_ask?.label || ''}
                    onChange={(e) => patchFinalWhistleSoft({ label: e.target.value })}
                    placeholder="One coaching tip every Tuesday"
                    data-testid="final-whistle-soft-ask-label"
                  />
                </div>
                <div>
                  <Label className="text-xs">Button text</Label>
                  <Input
                    value={content.final_whistle?.soft_ask?.button_text || ''}
                    onChange={(e) => patchFinalWhistleSoft({ button_text: e.target.value })}
                    placeholder="Subscribe"
                    data-testid="final-whistle-soft-ask-button"
                  />
                </div>
                {content.final_whistle?.soft_ask?.type === 'pdf' && (
                  <div className="md:col-span-2">
                    <Label className="text-xs">PDF download URL</Label>
                    <Input
                      value={content.final_whistle?.soft_ask?.url || ''}
                      onChange={(e) => patchFinalWhistleSoft({ url: e.target.value })}
                      placeholder="https://grassrootscoaching.app/files/sub-off-cheat-sheet.pdf"
                      data-testid="final-whistle-soft-ask-url"
                    />
                  </div>
                )}
              </div>
            )}
          </div>
        </CardContent>
      </Card>
      <Card>
        <CardHeader>
          <CardTitle>Footer</CardTitle>
          <CardDescription>Footer description and links</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div><Label>Description</Label><Input value={content.footer?.description || ''} onChange={(e) => updateFooter('description', e.target.value)} /></div>
        </CardContent>
      </Card>
    </div>
  );
}


/**
 * CustomCtaEditor — collapsible editor card for a single custom CTA.
 * Renders the same field set as the built-in Primary/Secondary CTA cards
 * (label, title, subtitle, button text, secondary text, action picker,
 * background image controls) plus a Delete button.
 */
function CustomCtaEditor({ cta, onPatch, onDelete, defaultOpen = false }) {
  const [open, setOpen] = useState(defaultOpen);
  const [confirmingDelete, setConfirmingDelete] = useState(false);
  const displayLabel = cta.label || cta.title || 'Untitled CTA';

  return (
    <div
      className="rounded-lg border border-indigo-200 bg-indigo-50/30 overflow-hidden"
      data-testid={`custom-cta-editor-${cta.id}`}
    >
      <button
        type="button"
        onClick={() => setOpen(v => !v)}
        className="w-full flex items-center justify-between px-3 py-2 hover:bg-indigo-100/50 transition-colors"
        data-testid={`custom-cta-toggle-${cta.id}`}
        aria-expanded={open}
      >
        <div className="flex items-center gap-2 min-w-0">
          {open
            ? <ChevronDown className="w-4 h-4 text-indigo-500 shrink-0" />
            : <ChevronRight className="w-4 h-4 text-indigo-500 shrink-0" />}
          <span className="font-semibold text-sm text-gray-900 truncate">{displayLabel}</span>
          <span className="text-[10px] font-mono text-gray-400 truncate hidden sm:inline">{cta.id}</span>
        </div>
      </button>

      {open && (
        <div className="p-3 sm:p-4 space-y-3 bg-white border-t border-indigo-200">
          <div>
            <Label className="text-xs">Section label (shown in Layout tab)</Label>
            <Input
              value={cta.label || ''}
              onChange={(e) => onPatch({ label: e.target.value })}
              placeholder="e.g. Newsletter promo"
              className="mt-1"
              data-testid={`custom-cta-label-${cta.id}`}
            />
          </div>

          <div>
            <Label className="text-xs">Title</Label>
            <Input
              value={cta.title || ''}
              onChange={(e) => onPatch({ title: e.target.value })}
              className="mt-1"
              data-testid={`custom-cta-title-${cta.id}`}
            />
          </div>
          <div>
            <Label className="text-xs">Subtitle</Label>
            <Input
              value={cta.subtitle || ''}
              onChange={(e) => onPatch({ subtitle: e.target.value })}
              className="mt-1"
              data-testid={`custom-cta-subtitle-${cta.id}`}
            />
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <div>
              <Label className="text-xs">Button text</Label>
              <Input
                value={cta.buttonText || ''}
                onChange={(e) => onPatch({ buttonText: e.target.value })}
                className="mt-1"
                data-testid={`custom-cta-button-${cta.id}`}
              />
            </div>
            <div>
              <Label className="text-xs">Secondary text (below button)</Label>
              <Input
                value={cta.secondaryText || ''}
                onChange={(e) => onPatch({ secondaryText: e.target.value })}
                className="mt-1"
                data-testid={`custom-cta-secondary-${cta.id}`}
              />
            </div>
          </div>

          <CtaActionPicker
            value={cta}
            onChange={onPatch}
            testIdPrefix={`custom-cta-${cta.id}`}
          />

          <CtaBackgroundControls
            value={cta}
            onChange={onPatch}
            testIdPrefix={`custom-cta-${cta.id}`}
          />

          <div className="pt-3 border-t border-gray-100 flex items-center justify-end gap-2">
            {!confirmingDelete ? (
              <Button
                variant="outline"
                size="sm"
                className="text-red-600 border-red-200 hover:bg-red-50"
                onClick={() => setConfirmingDelete(true)}
                data-testid={`custom-cta-delete-btn-${cta.id}`}
              >
                <Trash2 className="w-3.5 h-3.5 mr-1.5" />Delete this CTA
              </Button>
            ) : (
              <>
                <span className="text-xs text-red-600 mr-1">Sure? This removes it from the landing page.</span>
                <Button variant="outline" size="sm" onClick={() => setConfirmingDelete(false)} data-testid={`custom-cta-delete-cancel-${cta.id}`}>Cancel</Button>
                <Button
                  size="sm"
                  className="bg-red-600 hover:bg-red-700 text-white"
                  onClick={() => { setConfirmingDelete(false); onDelete(); }}
                  data-testid={`custom-cta-delete-confirm-${cta.id}`}
                >
                  Delete
                </Button>
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
